import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useCmsContent } from "@/hooks/use-cms-content";
import { useDynamicTheme } from "@/hooks/use-dynamic-theme";
import { useFaviconTitle } from "@/hooks/use-favicon-title";
import { useTranslateServices } from "@/hooks/use-translate-services";
import { useWhatsApp } from "@/hooks/use-whatsapp";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  Heart, Users, Brain, Sparkles, Droplets, Leaf, Award,
  Clock, Euro, CheckCircle, ArrowRight, Star, MessageCircle,
  ChevronDown, Home, Building2, Shield,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface ServiceDB {
  id: string;
  name: string;
  description: string | null;
  price: number;
  duration_minutes: number;
  available_at_home: boolean;
  available_at_studio: boolean;
  image_url: string | null;
  category: string | null;
}

const iconMap: Record<string, React.ElementType> = {
  Heart, Users, Brain, Sparkles, Droplets, Leaf, Award, Shield,
};

const getServiceIcon = (name: string): React.ElementType => {
  const n = name.toLowerCase();
  if (n.includes("relaxa")) return Heart;
  if (n.includes("infanti") || n.includes("criança")) return Users;
  if (n.includes("ansie") || n.includes("stress")) return Brain;
  if (n.includes("aroma")) return Droplets;
  if (n.includes("pedras") || n.includes("quentes")) return Sparkles;
  if (n.includes("reflexo") || n.includes("podal")) return Leaf;
  return Award;
};

const ServicesPage = () => {
  useDynamicTheme();
  useFaviconTitle();
  const { t } = useTranslation();
  const { content } = useCmsContent();
  const { url: whatsappUrl, open: openWhatsApp } = useWhatsApp();
  // Prefer CMS value, fall back to i18n translation
  const c = (cmsKey: string, i18nKey: string) => {
    const v = (content as any)?.[cmsKey];
    return v && String(v).trim() ? String(v) : t(i18nKey);
  };
  const [servicesRaw, setServicesRaw] = useState<ServiceDB[]>([]);
  const [loading, setLoading] = useState(true);

  // Auto-translate service names/descriptions when language != PT
  const { services: translatedServices, translating } = useTranslateServices(servicesRaw);
  const services = translatedServices as ServiceDB[];

  useEffect(() => {
    supabase
      .from("services")
      .select("*")
      .eq("is_active", true)
      .order("name")
      .then(({ data }) => {
        setServicesRaw((data as ServiceDB[]) || []);
        setLoading(false);
      });
  }, []);

  // Map DB category slugs to display names
  const categoryDisplayName = (cat: string): string => {
    const map: Record<string, string> = {
      massage: "Massagem",
      children: "Kids",
      infantil: "Kids",
    };
    const mapped = map[cat.toLowerCase()];
    if (mapped) return mapped;
    // Capitalize first letter
    return cat.charAt(0).toUpperCase() + cat.slice(1);
  };

  // Group services by category
  const categories = Array.from(new Set(services.map((s) => s.category || c("sp_general", "servicesPage.general"))));

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "HealthAndBeautyBusiness",
    name: content.business_name || "Mente Sã",
    description: t("servicesPage.metaDesc"),
    url: window.location.href,
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: t("servicesPage.title"),
      itemListElement: services.map((s, i) => ({
        "@type": "Offer",
        position: i + 1,
        itemOffered: {
          "@type": "Service",
          name: s.name,
          description: s.description || "",
          offers: {
            "@type": "Offer",
            price: s.price.toFixed(2),
            priceCurrency: "EUR",
          },
        },
      })),
    },
  };

  useEffect(() => {
    document.title = `${t("servicesPage.title")} | ${content.business_name || "Mente Sã"}`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", t("servicesPage.metaDesc"));

    // JSON-LD
    let script = document.getElementById("services-jsonld") as HTMLScriptElement;
    if (!script) {
      script = document.createElement("script");
      script.id = "services-jsonld";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);

    return () => {
      script?.remove();
    };
  }, [services, t]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-16 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/15 backdrop-blur-sm border border-primary/20 px-5 py-2 text-xs font-semibold text-primary mb-6">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              {c("sp_badge", "servicesPage.badge")}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold mb-6 text-foreground leading-tight">
              {c("sp_heroTitle", "servicesPage.heroTitle").split("|")[0]}
              <span className="text-gradient-wine">{c("sp_heroTitle", "servicesPage.heroTitle").split("|")[1] || ""}</span>
            </h1>
            <p className="text-muted-foreground text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-8">
              {c("sp_heroSubtitle", "servicesPage.heroSubtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/#agendar"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 text-base font-semibold text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-xl transition-all"
              >
                {c("sp_bookCta", "servicesPage.bookCta")}
                <ArrowRight size={16} />
              </Link>
              <a
                href={whatsappUrl}
                onClick={openWhatsApp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary/30 px-8 py-4 text-base font-semibold text-primary hover:bg-primary/10 transition-colors"
              >
                <MessageCircle size={16} />
                {c("sp_whatsappCta", "servicesPage.whatsappCta")}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="rounded-2xl border border-border p-6 animate-pulse">
                  <div className="w-14 h-14 rounded-xl bg-muted mb-4" />
                  <div className="h-6 bg-muted rounded w-3/4 mb-3" />
                  <div className="h-4 bg-muted rounded w-full mb-2" />
                  <div className="h-4 bg-muted rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : (
            categories.map((cat) => {
              const catServices = services.filter(
                (s) => (s.category || c("sp_general", "servicesPage.general")) === cat
              );
              return (
                <div key={cat} className="mb-16 last:mb-0">
                  {categories.length > 1 && (
                    <motion.h2
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="font-display text-2xl md:text-3xl font-bold mb-8 text-foreground"
                    >
                      {categoryDisplayName(cat)}
                    </motion.h2>
                  )}
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {catServices.map((service, i) => {
                      const Icon = getServiceIcon(service.name);
                      return (
                        <motion.div
                          key={service.id}
                          initial={{ opacity: 0, y: 30 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.1, duration: 0.5 }}
                          className="group rounded-2xl bg-card border border-border overflow-hidden hover:shadow-xl hover:border-primary/30 transition-all duration-500"
                        >
                          {/* Image / Icon header */}
                          <div className="relative h-44 bg-gradient-to-br from-primary/10 to-accent/30 overflow-hidden">
                            {service.image_url ? (
                              <img
                                src={service.image_url}
                                alt={service.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                                  <Icon size={40} className="text-primary/60" />
                                </div>
                              </div>
                            )}
                            <div className="absolute top-3 right-3 bg-background/90 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-sm">
                              <span className="flex items-center gap-1 text-primary font-bold text-sm">
                                <Euro size={14} />
                                {service.price.toFixed(2)}
                              </span>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="p-6">
                            <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                              {service.name}
                            </h3>

                            {service.description && (
                              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                                {service.description}
                              </p>
                            )}

                            {/* Details */}
                            <div className="flex flex-wrap gap-2 mb-4">
                              <span className="inline-flex items-center gap-1 bg-accent/50 text-xs px-3 py-1 rounded-full text-muted-foreground">
                                <Clock size={12} /> {service.duration_minutes} min
                              </span>
                              {service.available_at_studio && (
                                <span className="inline-flex items-center gap-1 bg-accent/50 text-xs px-3 py-1 rounded-full text-muted-foreground">
                                  <Building2 size={12} /> {c("sp_atStudio", "servicesPage.atStudio")}
                                </span>
                              )}
                              {service.available_at_home && (
                                <span className="inline-flex items-center gap-1 bg-accent/50 text-xs px-3 py-1 rounded-full text-muted-foreground">
                                  <Home size={12} /> {c("sp_atHome", "servicesPage.atHome")}
                                </span>
                              )}
                            </div>

                            {/* Benefits */}
                            <div className="space-y-2 mb-5">
                              <div className="flex items-start gap-2">
                                <CheckCircle size={14} className="text-primary mt-0.5 shrink-0" />
                                <span className="text-xs text-muted-foreground">{c("sp_benefit1", "servicesPage.benefit1")}</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <CheckCircle size={14} className="text-primary mt-0.5 shrink-0" />
                                <span className="text-xs text-muted-foreground">{c("sp_benefit2", "servicesPage.benefit2")}</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <CheckCircle size={14} className="text-primary mt-0.5 shrink-0" />
                                <span className="text-xs text-muted-foreground">{c("sp_benefit3", "servicesPage.benefit3")}</span>
                              </div>
                            </div>

                            <Link
                              to="/#agendar"
                              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity w-full justify-center"
                            >
                              {c("sp_bookThis", "servicesPage.bookThis")}
                              <ArrowRight size={14} />
                            </Link>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* Social Proof / Testimonials strip */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              {c("sp_socialProofTitle", "servicesPage.socialProofTitle")}
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              {c("sp_socialProofSubtitle", "servicesPage.socialProofSubtitle")}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { name: "Sofia R.", text: c("sp_testimonial1", "servicesPage.testimonial1"), stars: 5 },
              { name: "Ana M.", text: c("sp_testimonial2", "servicesPage.testimonial2"), stars: 5 },
              { name: "Mariana P.", text: c("sp_testimonial3", "servicesPage.testimonial3"), stars: 5 },
            ].map((item, i) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="rounded-2xl bg-card border border-border p-6"
              >
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: item.stars }).map((_, j) => (
                    <Star key={j} size={14} className="fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-foreground text-sm leading-relaxed mb-3">"{item.text}"</p>
                <p className="text-xs font-semibold text-muted-foreground">{item.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container mx-auto px-6 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              {c("sp_faqTitle", "servicesPage.faqTitle")}
            </h2>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="rounded-xl border border-border px-5">
                <AccordionTrigger className="font-display font-semibold text-foreground text-left py-4 hover:no-underline">
                  {c(`sp_faq${i}Q`, `servicesPage.faq${i}Q`)}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm leading-relaxed pb-4">
                  {c(`sp_faq${i}A`, `servicesPage.faq${i}A`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-primary/10 to-accent/20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4 text-foreground">
              {c("sp_ctaTitle", "servicesPage.ctaTitle")}
            </h2>
            <p className="text-muted-foreground text-lg mb-8">
              {c("sp_ctaSubtitle", "servicesPage.ctaSubtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/#agendar"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-10 py-4 text-lg font-semibold text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-xl transition-all"
              >
                {c("sp_bookCta", "servicesPage.bookCta")}
                <ArrowRight size={18} />
              </Link>
              <a
                href={whatsappUrl}
                onClick={openWhatsApp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary/30 px-10 py-4 text-lg font-semibold text-primary hover:bg-primary/10 transition-colors"
              >
                <MessageCircle size={18} />
                WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
